import type {
  AllIntervieweesResponse,
  IntervieweeDetailResponse,
  IntervieweeListResponse,
  MapVillagesResponse,
  VillageSatelliteResponse,
  VillageEvidenceResponse,
  VillageSummaryResponse,
  VillageTagsResponse,
} from "../domain/contracts";
import { frontendEnv } from "./env";
import { fetchJson, fetchProtectedAsset } from "./apiClient";

export function fetchMapVillages(): Promise<MapVillagesResponse> {
  return fetchJson<MapVillagesResponse>(
    `${frontendEnv.publicApiBaseUrl}/api/map/villages?lang=${encodeURIComponent(frontendEnv.defaultLanguage)}`,
  );
}

export function fetchVillageSummary(villageRef: string): Promise<VillageSummaryResponse> {
  return fetchJson<VillageSummaryResponse>(
    `${frontendEnv.publicApiBaseUrl}/api/villages/${encodeURIComponent(villageRef)}/summary`,
  );
}

export function fetchVillageInterviewees(villageRef: string, page = 1): Promise<IntervieweeListResponse> {
  return fetchJson<IntervieweeListResponse>(
    `${frontendEnv.publicApiBaseUrl}/api/villages/${encodeURIComponent(villageRef)}/interviewees?page=${encodeURIComponent(String(page))}`,
  );
}

export function fetchVillageEvidence(villageRef: string, category?: string): Promise<VillageEvidenceResponse> {
  const url = new URL(`${frontendEnv.publicApiBaseUrl}/api/villages/${encodeURIComponent(villageRef)}/evidence`);
  if (category) {
    url.searchParams.set("category", category);
  }
  return fetchJson<VillageEvidenceResponse>(url.toString());
}

export function fetchVillageTags(villageRef: string): Promise<VillageTagsResponse> {
  return fetchJson<VillageTagsResponse>(
    `${frontendEnv.publicApiBaseUrl}/api/villages/${encodeURIComponent(villageRef)}/summary-tags`,
  );
}

export function fetchIntervieweeDetail(intervieweeRef: string): Promise<IntervieweeDetailResponse> {
  return fetchJson<IntervieweeDetailResponse>(
    `${frontendEnv.publicApiBaseUrl}/api/interviewees/${encodeURIComponent(intervieweeRef)}`,
  );
}

export function fetchAllInterviewees(page = 1, pageSize = 24, query = ""): Promise<AllIntervieweesResponse> {
  const url = new URL(`${frontendEnv.publicApiBaseUrl}/api/interviewees`);
  url.searchParams.set("page", String(Math.max(1, Math.trunc(page))));
  url.searchParams.set("pageSize", String(Math.max(1, Math.trunc(pageSize))));

  const normalizedQuery = query.trim();
  if (normalizedQuery) {
    url.searchParams.set("q", normalizedQuery);
  }

  return fetchJson<AllIntervieweesResponse>(url.toString());
}

export async function fetchMediaObjectUrl(variant: string, assetId: string): Promise<string> {
  const blob = await fetchProtectedAsset(
    `${frontendEnv.publicMediaBaseUrl}/media/${encodeURIComponent(variant)}/${encodeURIComponent(assetId)}`,
  );
  return URL.createObjectURL(blob);
}

export function fetchVillageSatellite(villageRef: string, imageType = "satellite"): Promise<VillageSatelliteResponse> {
  const url = new URL(`${frontendEnv.publicApiBaseUrl}/api/villages/${encodeURIComponent(villageRef)}/satellite`);
  if (imageType) {
    url.searchParams.set("imageType", imageType);
  }
  return fetchJson<VillageSatelliteResponse>(url.toString());
}

export function fetchVillageSatelliteBlobByKey(r2Key: string, mimeType?: string, allowPlaceholder = false): Promise<Blob> {
  const url = new URL(`${frontendEnv.publicMediaBaseUrl}/media/village`);
  url.searchParams.set("key", r2Key);
  if (mimeType && mimeType.trim()) {
    url.searchParams.set("mimeType", mimeType.trim());
  }
  if (allowPlaceholder) {
    url.searchParams.set("allowPlaceholder", "1");
  }
  return fetchProtectedAsset(url.toString());
}